import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Environment, ENVIRONMENT } from '@ld.consumer-portal/models';
import { catchError, EMPTY, Observable, OperatorFunction, throwError } from 'rxjs';
import { AuthService, CIAM_ACCESS_TOKEN_KEY } from '../services/auth.service';
import { LoggingService } from '../services/logging.service';
import { ThemeService } from '../services/theme.service';
import { ToastService } from '../services/toast.service';

function isNativeMobile(): boolean {
  const capacitor = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  return capacitor?.isNativePlatform?.() ?? false;
}

type EndpointType = 'mla' | 'mla-auth' | 'origination' | 'servicing' | 'sedm' | 'customer-identity' | 'public' | 'none';

const ORIGINATION_PATTERNS = [
  '/api/v1/auth/',
  '/api/v1/loans/',
  '/api/v1/borrowers/',
  '/api/v1/documents/',
  '/api/v1/conditions/',
  '/api/v1/tasks/',
  '/api/v1/service/',
];
const SERVICING_PATTERNS = [
  '/paymentintegration',
  '/MelloServPaymentIntegration',
  '/ldprofile',
  '/MelloServDomain',
  '/ServicingLINCPortal',
  '/CreditMonitoring',
  '/crmintegration',
  '/api/',
];
const LOAN_PATTERNS = ['ServicingLINCPortal', 'Loan/GetLoans'];

const LINC_APIM_PATTERNS = ['ServicingLINCPortal'];

export const STEP_UP_MFA_TOKEN_KEY = 'mfa_step_up_token';

const SEDM_PATTERNS = ['/SEDMDocumentList', '/SEDMDownloadDocument', '/SEDMDocumentView'];

const PUBLIC_ENDPOINT_PATTERNS = [
  '/register/check-token',
  '/register/check',
  '/register/confirm-login',
  '/ServicingLINCPortal/Unsubscribe',
];

const CUSTOMER_IDENTITY_PUBLIC_PATTERNS = [
  '/api/NativeSignupStart',
  '/api/NativeSignupContinue',
  '/api/TenantConfiguration',
  '/api/VerifyEmail',
];

function isPublicEndpoint(url: string): boolean {
  return PUBLIC_ENDPOINT_PATTERNS.some((pattern) => url.includes(pattern));
}

const MFA_ENDPOINT_PATTERNS = [
  'ProcessLoginMFA',
  'SendLoginMFACode',
  'VerifyLoginMFACode',
  'SendMFACode',
  'VerifyMFACode',
  'GetMfaOptions',
];

// Feedback submissions (MelloServCRMIntegration, under the /crmintegration
// APIM route) are guarded by an Internal-Api-Key client-key check rather than
// the user's session token, so a 401 there does not mean the session expired.
const FEEDBACK_ENDPOINT_PATTERNS = ['/Feedback/'];

const AUTH_ENDPOINT_PATTERNS = [
  '/oauth2/v2.0/token', // B2C token endpoint (b2c_token)
  '/authentication/token', // MLA EPS token endpoint (eps_token)
  '/session/sso', // MLA session SSO endpoint (mla_token)
  '/auth/sso', // CP SSO endpoint (cp_token)
];

function isMfaEndpoint(url: string): boolean {
  return MFA_ENDPOINT_PATTERNS.some((pattern) => url.includes(pattern));
}

function isFeedbackEndpoint(url: string, crmUrl?: string): boolean {
  const isCrmRoute = url.includes('/crmintegration/') || (!!crmUrl && url.startsWith(`${crmUrl}/`));
  return isCrmRoute && FEEDBACK_ENDPOINT_PATTERNS.some((pattern) => url.includes(pattern));
}

function isAuthEndpoint(url: string): boolean {
  return AUTH_ENDPOINT_PATTERNS.some((pattern) => url.includes(pattern));
}

function isMlaAuthEndpoint(url: string): boolean {
  return url.includes('/authentication/token') || url.includes('/user/status') || url.includes('/user/azure');
}

function isAuthenticatedEndpoint(url: string): boolean {
  if (isPublicEndpoint(url)) {
    return false;
  }

  return (
    SERVICING_PATTERNS.some((p) => url.includes(p)) ||
    ORIGINATION_PATTERNS.some((p) => url.includes(p)) ||
    url.includes('/mla/') ||
    url.includes('loandepotdev.works')
  );
}

function isMlaApimEndpoint(url: string): boolean {
  return url.includes('ldapi-') && url.includes('/mla/');
}

function isMlaEndpoint(url: string, env: Environment): boolean {
  const { mlaApiUrl } = env.appSettings;
  return isMlaApimEndpoint(url) || (!!mlaApiUrl && url.startsWith(mlaApiUrl)) || url.includes('mla-publicapi');
}

function detectEndpointType(url: string, env: Environment): EndpointType {
  const { mlaApiUrl, cpApiUrl, profileDomainUrl, melloServPaymentIntegrationUrl } = env.appSettings;

  if (url.includes('/authentication/token') || url.includes('/user/status')) {
    return 'mla-auth';
  }

  if ((mlaApiUrl && url.startsWith(mlaApiUrl)) || url.startsWith('/mla-api/') || url.includes('mla-publicapi')) {
    return 'mla';
  }

  if (url.includes('/auth/sso') || url.includes('/auth/ula')) {
    return 'none';
  }

  if (isPublicEndpoint(url)) {
    return 'public';
  }

  if (
    (cpApiUrl && url.startsWith(cpApiUrl)) ||
    url.startsWith('/cp-api/') ||
    url.includes('cp-public') ||
    ORIGINATION_PATTERNS.some((p) => url.includes(p))
  ) {
    return 'origination';
  }

  if (SEDM_PATTERNS.some((p) => url.includes(p))) {
    return 'sedm';
  }

  const { customerIdentityApiUrl, customerIdentityDirectApiUrl } = env.appSettings;
  if (
    (customerIdentityApiUrl && url.startsWith(customerIdentityApiUrl)) ||
    (customerIdentityDirectApiUrl && url.startsWith(customerIdentityDirectApiUrl))
  ) {
    return 'customer-identity';
  }

  if (
    (profileDomainUrl && url.startsWith(profileDomainUrl)) ||
    (melloServPaymentIntegrationUrl && url.startsWith(melloServPaymentIntegrationUrl)) ||
    url.includes('loandepotdev.works') ||
    SERVICING_PATTERNS.some((p) => url.includes(p))
  ) {
    return 'servicing';
  }

  return 'none';
}

/**
 * Returns the preferred CIAM token for backend API calls.
 *
 * On native mobile the id_token (`b2c_id_token`) carries the mobile app's
 * audience claim which the backend APIs do not recognise.  The access token
 * (`b2c_token`) targets `api://consumerportal` – the same audience regardless
 * of whether the request originated from the web or native client – so we
 * prefer it on native.
 */
function getPreferredCiamToken(): string | null {
  if (isNativeMobile()) {
    return localStorage.getItem('b2c_token') || localStorage.getItem('b2c_id_token');
  }
  return localStorage.getItem('b2c_id_token');
}

function buildMlaHeaders(req: HttpRequest<unknown>, env: Environment, themeService: ThemeService): Record<string, string> {
  const headers: Record<string, string> = {};

  if (!req.headers.has('Content-Type')) {
    headers['Content-Type'] = 'application/json';
  }

  const epsToken = sessionStorage.getItem('eps_token');
  const b2cToken = localStorage.getItem('b2c_token');

  if (isMlaEndpoint(req.url, env)) {
    const ciamTokenForMla = getPreferredCiamToken();
    if (ciamTokenForMla) {
      headers['Authorization'] = `Bearer ${ciamTokenForMla}`;
    } else if (b2cToken) {
      headers['Authorization'] = `Bearer ${b2cToken}`;
    } else if (epsToken) {
      headers['Authorization'] = `Bearer ${epsToken}`;
    }
  } else if (epsToken) {
    headers['Authorization'] = `Bearer ${epsToken}`;
  } else if (b2cToken) {
    headers['Authorization'] = `Bearer ${b2cToken}`;
  }

  const ciamToken = getPreferredCiamToken();
  if (ciamToken) {
    headers['b2ctoken'] = ciamToken;
    headers['authorization-provider'] = env.appSettings.useCustomerIdentityAuth ? 'Entra' : 'CP2';
    if (env.appSettings.useCustomerIdentityAuth) {
      headers['X-Partner-Id'] = themeService.getXPartnerId();
    }
  }

  const hostInfo = getMlaHostInfo(themeService);
  if (hostInfo) {
    headers['hostinfo'] = hostInfo;
  }

  const visitorId = sessionStorage.getItem('MacVisitorId');
  if (visitorId) {
    headers['macvisitorid'] = visitorId;
  }

  const { ocpApimSubscriptionKey } = env.appSettings;
  if (ocpApimSubscriptionKey) {
    headers['Ocp-Apim-Subscription-Key'] = ocpApimSubscriptionKey;
  }

  return headers;
}

function buildOriginationHeaders(req: HttpRequest<unknown>, env: Environment, themeService: ThemeService): Record<string, string> {
  const headers: Record<string, string> = {};

  const cpToken = localStorage.getItem('cp_token');
  if (cpToken && !req.headers.has('Authorization')) {
    headers['Authorization'] = `Bearer ${cpToken}`;
    headers['authorization-provider'] = env.appSettings.useCustomerIdentityAuth ? 'Entra' : 'CP2';
    if (env.appSettings.useCustomerIdentityAuth) {
      headers['X-Partner-Id'] = themeService.getXPartnerId();
    }
  } else if (!cpToken && !req.headers.has('Authorization')) {
    const ciamToken = getPreferredCiamToken();
    if (ciamToken) {
      headers['Authorization'] = `Bearer ${ciamToken}`;
      headers['authorization-provider'] = env.appSettings.useCustomerIdentityAuth ? 'Entra' : 'CP2';
      if (env.appSettings.useCustomerIdentityAuth) {
        headers['X-Partner-Id'] = themeService.getXPartnerId();
      }
    }
  }

  const b2cToken = localStorage.getItem('b2c_token');
  if (b2cToken) {
    headers['B2CToken'] = b2cToken;
  }

  const macVisitorId = sessionStorage.getItem('MacVisitorId');
  if (macVisitorId) {
    headers['MacVisitorId'] = macVisitorId;
  }

  const cpVisitorId = localStorage.getItem('cp_visitor_id');
  if (cpVisitorId) {
    headers['visitorId'] = cpVisitorId;
  }

  const { cpApimSubscriptionKey, ocpApimSubscriptionKey } = env.appSettings;
  if (cpApimSubscriptionKey) {
    headers['Apim-CP-Provider-Subscription'] = cpApimSubscriptionKey;
  }

  if (ocpApimSubscriptionKey) {
    headers['Ocp-Apim-Subscription-Key'] = ocpApimSubscriptionKey;
  }

  return headers;
}

function buildServicingHeaders(url: string, req: HttpRequest<unknown>, env: Environment): Record<string, string> {
  const {
    ocpApimSubscriptionKey,
    channelId = '2',
    LoanConsumerType = '2',
    MelloServCRMUrl,
    internalApiKey,
  } = env.appSettings;
  const headers: Record<string, string> = {};

  const isLincApim = LINC_APIM_PATTERNS.some((p) => url.includes(p));
  let b2cToken: string | null = null;

  const stepUpToken = sessionStorage.getItem(STEP_UP_MFA_TOKEN_KEY);
  if (stepUpToken) {
    sessionStorage.removeItem(STEP_UP_MFA_TOKEN_KEY);
    b2cToken = stepUpToken;
  } else if (isLincApim) {
    b2cToken = getPreferredCiamToken() || localStorage.getItem('b2c_token');
  } else {
    b2cToken = localStorage.getItem('b2c_token');
  }

  if (!b2cToken && isMfaEndpoint(url)) {
    b2cToken = sessionStorage.getItem('pending_b2c_token');
  }

  if (isFeedbackEndpoint(url, MelloServCRMUrl)) {
    b2cToken = localStorage.getItem(CIAM_ACCESS_TOKEN_KEY) || b2cToken;
  }

  if (b2cToken && !req.headers.has('Authorization')) {
    headers['Authorization'] = `Bearer ${b2cToken}`;
  }

  if (ocpApimSubscriptionKey && isFeedbackEndpoint(url, MelloServCRMUrl)) {
    headers['Ocp-Apim-Subscription-Key'] = ocpApimSubscriptionKey;
  }

  const isNative = isNativeMobile();
  const isMfaComplete = !!localStorage.getItem('b2c_token');
  const effectiveChannelId = isNative && isMfaComplete ? '3' : channelId;
  headers['channel-id'] = effectiveChannelId;

  const userId = localStorage.getItem('user_id');
  if (userId) {
    headers['global-user-id'] = userId;
  }

  if (LOAN_PATTERNS.some((p) => url.includes(p))) {
    headers['loan-consumer-type'] = LoanConsumerType;
  }

  if (internalApiKey && (url.includes(MelloServCRMUrl || '') || url.includes('/crmintegration'))) {
    headers['Internal-API-Key'] = internalApiKey;
    headers['loan-consumer-type'] = LoanConsumerType;
  }

  return headers;
}

function buildSedmHeaders(env: Environment): Record<string, string> {
  const { ocpApimSubscriptionSEDM, channelId = '2' } = env.appSettings;
  const headers: Record<string, string> = {};

  const b2cToken = localStorage.getItem('b2c_token');
  if (b2cToken) {
    headers['Authorization'] = `Bearer ${b2cToken}`;
  }

  if (ocpApimSubscriptionSEDM) {
    headers['Ocp-Apim-Subscription-Key'] = ocpApimSubscriptionSEDM;
  }

  headers['channel-id'] = channelId;

  const userId = localStorage.getItem('user_id');
  if (userId) {
    headers['global-user-id'] = userId;
  }

  return headers;
}

function buildCustomerIdentityHeaders(env: Environment, url: string): Record<string, string> {
  const headers: Record<string, string> = {};

  const isPublicCiEndpoint = CUSTOMER_IDENTITY_PUBLIC_PATTERNS.some((p) => url.includes(p));
  if (!isPublicCiEndpoint) {
    const entraToken = getPreferredCiamToken();
    if (entraToken) {
      headers['Authorization'] = `Bearer ${entraToken}`;
    }
  }

  const { cpApimSubscriptionKeyCustomerIdentity, ocpApimSubscriptionKey } = env.appSettings;
  const subscriptionKey = cpApimSubscriptionKeyCustomerIdentity || ocpApimSubscriptionKey;
  if (subscriptionKey) {
    headers['Ocp-Apim-Subscription-Key'] = subscriptionKey;
  }

  return headers;
}

function buildPublicEndpointHeaders(env: Environment): Record<string, string> {
  const headers: Record<string, string> = {};

  const { cpApimSubscriptionKey, ocpApimSubscriptionKey } = env.appSettings;
  if (cpApimSubscriptionKey) {
    headers['Apim-CP-Provider-Subscription'] = cpApimSubscriptionKey;
  }

  if (ocpApimSubscriptionKey) {
    headers['Ocp-Apim-Subscription-Key'] = ocpApimSubscriptionKey;
  }

  return headers;
}

function buildMlaAuthHeaders(req: HttpRequest<unknown>, env: Environment, themeService: ThemeService): Record<string, string> {
  const headers: Record<string, string> = {};

  const { ocpApimSubscriptionKey } = env.appSettings;
  if (ocpApimSubscriptionKey) {
    headers['Ocp-Apim-Subscription-Key'] = ocpApimSubscriptionKey;
  }

  const ciamTokenForAuth = getPreferredCiamToken();
  if (ciamTokenForAuth && !req.headers.has('Authorization')) {
    headers['Authorization'] = `Bearer ${ciamTokenForAuth}`;
    headers['authorization-provider'] = env.appSettings.useCustomerIdentityAuth ? 'Entra' : 'CP2';
    if (env.appSettings.useCustomerIdentityAuth) {
      headers['X-Partner-Id'] = themeService.getXPartnerId();
    }
  }

  if (ciamTokenForAuth) {
    headers['b2ctoken'] = ciamTokenForAuth;
  }

  const hostInfo = getMlaHostInfo(themeService);
  if (hostInfo) {
    headers['hostinfo'] = hostInfo;
  }

  return headers;
}

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const loggingService = inject(LoggingService);
  const env = inject(ENVIRONMENT);
  const authService = inject(AuthService);
  const themeService = inject(ThemeService);
  const router = inject(Router);
  const toastService = inject(ToastService);

  const endpointType = detectEndpointType(req.url, env);
  const errorHandler = handleAuthError(req.url, endpointType, env, authService, router, toastService, loggingService);

  switch (endpointType) {
    case 'mla': {
      const headers = buildMlaHeaders(req, env, themeService);
      return next(req.clone({ setHeaders: headers })).pipe(errorHandler);
    }

    case 'origination': {
      const headers = buildOriginationHeaders(req, env, themeService);
      return next(req.clone({ setHeaders: headers })).pipe(errorHandler);
    }

    case 'servicing': {
      const headers = buildServicingHeaders(req.url, req, env);
      return next(req.clone({ setHeaders: headers })).pipe(errorHandler);
    }

    case 'sedm': {
      const headers = buildSedmHeaders(env);
      return next(req.clone({ setHeaders: headers })).pipe(errorHandler);
    }

    case 'customer-identity': {
      const headers = buildCustomerIdentityHeaders(env, req.url);
      return next(req.clone({ setHeaders: headers })).pipe(errorHandler);
    }

    case 'public': {
      const headers = buildPublicEndpointHeaders(env);
      return next(req.clone({ setHeaders: headers })).pipe(errorHandler);
    }

    case 'mla-auth': {
      const headers = buildMlaAuthHeaders(req, env, themeService);
      return next(req.clone({ setHeaders: headers })).pipe(errorHandler);
    }
  }

  return next(req).pipe(errorHandler);
};

const AUTH_FLOW_ROUTES = ['/login', '/ciam/', '/auth/callback', '/sign-out'];

const LOGIN_GRACE_PERIOD_MS = 30_000;

let lastLoginTimestamp = 0;

export function markLoginTimestamp(): void {
  lastLoginTimestamp = Date.now();
}

function isWithinLoginGracePeriod(): boolean {
  return lastLoginTimestamp > 0 && Date.now() - lastLoginTimestamp < LOGIN_GRACE_PERIOD_MS;
}

function isAuthFlowRoute(currentUrl: string): boolean {
  return AUTH_FLOW_ROUTES.some((route) => currentUrl.includes(route));
}

function handleAuthError(
  url: string,
  endpointType: EndpointType,
  env: Environment,
  authService: AuthService,
  router: Router,
  toastService: ToastService,
  loggingService: LoggingService,
): OperatorFunction<HttpEvent<unknown>, HttpEvent<unknown>> {
  return catchError((error: HttpErrorResponse): Observable<HttpEvent<unknown>> => {
    const is401OnAuthEndpoint = error.status === 401 && isAuthEndpoint(url);
    const is401OnAuthenticatedEndpoint = error.status === 401 && isAuthenticatedEndpoint(url);
    const isStatus0OnAuthenticatedEndpoint = error.status === 0 && isAuthenticatedEndpoint(url);

    // Status 0 means no HTTP response was received, so it is a transport-level
    // failure rather than proof that the user's session expired.
    if (isStatus0OnAuthenticatedEndpoint) {
      loggingService.logWarning(
        `[AuthInterceptor] Status 0 on authenticated endpoint for ${url}, treating as network error`,
      );
      return throwError(() => error) as Observable<HttpEvent<unknown>>;
    }

    if (is401OnAuthEndpoint || is401OnAuthenticatedEndpoint) {
      if (isMlaAuthEndpoint(url) || isMfaEndpoint(url) || isFeedbackEndpoint(url, env.appSettings.MelloServCRMUrl) || endpointType === 'customer-identity') {
        return throwError(() => error) as Observable<HttpEvent<unknown>>;
      }

      // Don't trigger auto-logout during auth-flow routes or within the
      // post-login grace period to avoid clearing freshly acquired tokens.
      if (isAuthFlowRoute(router.url) || isWithinLoginGracePeriod()) {
        loggingService.logWarning(
          `[AuthInterceptor] ${error.status} on ${url} suppressed (authFlowRoute=${isAuthFlowRoute(router.url)}, graceWindow=${isWithinLoginGracePeriod()})`,
        );
        return throwError(() => error) as Observable<HttpEvent<unknown>>;
      }

      loggingService.logWarning(`[AuthInterceptor] 401 Unauthorized on ${url}, redirecting to login`);

      authService.clearAllTokens();
      clearMlaSession();
      sessionStorage.clear();
      localStorage.removeItem('ciam_session_active');
      localStorage.removeItem('native_ciam_refresh_token');
      // Keep native_ciam_auth_options for biometric silent refresh after app restart

      toastService.info('Your session has expired. Please log in again.', 'Session Expired');

      router.navigate(['/login'], {
        queryParams: { returnUrl: router.url, sessionExpired: 'true' },
        replaceUrl: true,
      });

      return EMPTY as Observable<HttpEvent<unknown>>;
    }

    return throwError(() => error) as Observable<HttpEvent<unknown>>;
  });
}

function clearMlaSession(): void {
  sessionStorage.removeItem('eps_token');
  sessionStorage.removeItem('mla_userId');
  sessionStorage.removeItem('mla_borrowerId');
  sessionStorage.removeItem('mla_cpBorrowerId');
  sessionStorage.removeItem('mla_loanId');
  sessionStorage.removeItem('mla_channel');
  sessionStorage.removeItem('mla_profileName');
  sessionStorage.removeItem('mla_dashboardToken');
  sessionStorage.removeItem('MacVisitorId');
  sessionStorage.removeItem('mla_token');
}

function getMlaHostInfo(themeService: ThemeService): string {
  const partnerId = themeService.getHostInfoPartnerId();
  const host = window.location.origin;

  try {
    return btoa(JSON.stringify({ partnerId, host }));
  } catch {
    return '';
  }
}
